import { ICopilotBlob, ICopilotResponse, ICopilotUserSessionDetails, ICopilotUserSession } from 'interfaces';
import { CopilotClient } from '@/infrastructure/interfaces/CopilotClient';
import { EventAggregationService } from '@/utils/event-aggregation-service';
import { ITokenBasedSessionService, SessionServiceReadyEvent } from "@studyportals/student-interfaces";
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { v4 } from 'uuid';

export class CopilotAPIClient
	extends CopilotClient
	implements ISubscriber<SessionServiceReadyEvent> {
	private sessionService?: ITokenBasedSessionService;

	constructor(
		endpoint: string,
		private historyEndpoint: string,
		startSessionId?: string
	) {
		const sessionId: string = startSessionId || CopilotAPIClient.generateSessionId();

		super(sessionId, endpoint);

		this.subscribeToSessionService();
	}

	private subscribeToSessionService(): void {
		EventAggregationService.instance?.subscribeTo(SessionServiceReadyEvent.EventType, this, true);
	}

	public notify(event: SessionServiceReadyEvent): void | Promise<void> {
		this.sessionService = event.sessionService;
	}

	public async generateAnswer(blob: ICopilotBlob): Promise<ICopilotResponse> {
		if (blob.message.length === 0) {
			throw new Error('Message cannot be empty');
		}

		try {
			const session = await this.sessionService?.getSession();
			const token = await session?.getAccessToken();
			const headers: Record<string, string> = {
				'Content-Type': 'application/json'
			};

			if (token) {
				headers.Authorization = token;
			}

			const response = await fetch(`${this.endpoint}/chat`, {
				method: 'POST',
				body: JSON.stringify(blob),
				headers
			});

			if (!response.ok) {
				throw new Error();
			}

			return response.json() as Promise<ICopilotResponse>;
		} catch {
			throw new Error('Failed to generate answer');
		}
	}

	public async getUserId(): Promise<string | void> {
		const session = await this.sessionService?.getSession();
		if (!session) return;

		return session.getUser().identityId;
	}

	public async getUserSessions(): Promise<ICopilotUserSession[]> {
		const session = await this.sessionService?.getSession();

		if (!session) {
			throw new Error('Session not found');
		}

		try {
			const token = await session.getAccessToken();

			const response = await fetch(`${this.historyEndpoint}/sessions`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': token
				}
			});

			if (!response.ok) {
				throw new Error();
			}

			return response.json() as Promise<ICopilotUserSession[]>;
		} catch {
			throw new Error('Failed to get chat user history');
		}
	}

	public async getUserSession(sessionId: string): Promise<ICopilotUserSessionDetails> {
		const session = await this.sessionService?.getSession();

		if (!session) {
			throw new Error('Session not found');
		}

		try {
			const token = await session.getAccessToken();
			const encodeSessionId = encodeURIComponent(sessionId);

			const response = await fetch(`${this.historyEndpoint}/sessions/${encodeSessionId}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': token
				}
			});

			if (!response.ok) {
				throw new Error();
			}

			return response.json() as Promise<ICopilotUserSessionDetails>;
		} catch{
			throw new Error('Failed to get chat session');
		}
	}

	private static generateSessionId(): string {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		const uuid: string = v4();
		const unixDate: number = Math.floor(+Date.now() / 1000);

		return `${uuid}#${unixDate}`;
	}
}
